import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react"
import { ChevronDown } from "lucide-react"
import {
  ComponentPropsWithoutRef,
  ReactNode,
  RefObject,
  useId,
  useRef,
} from "react"

import { classNames } from "../../../../lib/classNames"
import {
  HEADLESS_OPTIONS_LIMIT,
  isHeadlessOptionsTruncated,
  renderHeadlessSafeItems,
} from "../../../../lib/headless"
import { Icon, IconSizes } from "../../../Icon"
import { Chip } from "../../informational/Chip"
import { KeyboardTile } from "../../informational/KeyboardTile"
import { Label } from "../../informational/Label"

export type SelectMenuProps<T> = {
  label?: string
  secondaryLabel?: string
  items: T[]
  selectedItem: T | null
  onSelectItem: ((item: T) => void) | ((item: T | null) => void)
  keyExtractor: (item: T) => string
  valueExtractor: (item: T) => string
  chipValueExtractor?: (item: T) => string
  invalid?: boolean
  errorMessage?: string
  disabled?: boolean
  placeholder?: string
  isDeselectable?: boolean
  deselectText?: string
  shouldUsePlaceholderForDeselect?: boolean
  height?: number
  renderItem?: (item: T) => ReactNode
  renderSelectedItem?: (item: T | null) => ReactNode
  keyboardShortcut?: string
  autoFocus?: boolean
  buttonRef?: RefObject<HTMLButtonElement | null>
  dataTestId?: string
  eq?: (a: T | null, b: T | null) => boolean
  optionsClassName?: string
  listBoxOptionsAnchor?: ComponentPropsWithoutRef<
    typeof ListboxOptions
  >["anchor"]
} & ComponentPropsWithoutRef<"div">

export function SelectMenu<T>(props: SelectMenuProps<T>) {
  const {
    eq,
    label,
    secondaryLabel,
    items,
    selectedItem,
    onSelectItem,
    keyExtractor,
    valueExtractor,
    chipValueExtractor,
    invalid = false,
    errorMessage,
    disabled = false,
    placeholder,
    isDeselectable,
    shouldUsePlaceholderForDeselect = false,
    deselectText,
    height,
    renderItem,
    renderSelectedItem,
    keyboardShortcut,
    autoFocus,
    buttonRef,
    dataTestId,
    optionsClassName,
    listBoxOptionsAnchor,
    ...otherProps
  } = props

  const innerRef = useRef<HTMLButtonElement>(null)
  const inputRef = buttonRef ?? innerRef

  const buttonId = useId()

  const handleChange = (item: T | null) => {
    if (isDeselectable) {
      return (onSelectItem as (item: T | null) => void)(item)
    }

    if (item === null || item === undefined) {
      throw new Error("Null select when not allowed")
    }

    return onSelectItem(item)
  }

  const getPlaceholder = () => {
    if (selectedItem) {
      return valueExtractor(selectedItem)
    }
    if (shouldUsePlaceholderForDeselect) {
      return placeholder
    }
    if (isDeselectable) {
      return deselectText ?? "None"
    }
    return placeholder ?? "Select one"
  }

  const defaultOptionClassName =
    "bg-neutral-default relative cursor-default select-none px-4 py-2"

  return (
    <div {...otherProps}>
      <Listbox
        by={eq}
        value={selectedItem}
        onChange={handleChange}
        disabled={disabled}
      >
        <Label
          label={label}
          secondaryLabel={secondaryLabel}
          errorMessage={errorMessage}
          inputId={buttonId}
        >
          <div className="relative">
            <ListboxButton
              id={buttonId}
              className={classNames(
                "bg-input-default border-v2-border-default shadow-control group relative w-full cursor-default rounded border py-2 pl-3 pr-10 text-left",
                "hover:not-disabled:border-v2-border-strong",
                "disabled:border-v2-border-default-disabled disabled:bg-input-disabled disabled:color-disabled",
                (invalid || !!errorMessage) &&
                  "border-v2-border-danger hover:not-disabled:border-v2-border-danger",
                !!height && `h-${height}`
              )}
              ref={inputRef}
              autoFocus={autoFocus}
              data-testid={dataTestId}
              aria-keyshortcuts={keyboardShortcut?.toUpperCase()}
            >
              {renderSelectedItem ? (
                renderSelectedItem(selectedItem)
              ) : (
                <span
                  className={classNames(
                    "text-body group-disabled:color-disabled block truncate",
                    selectedItem ? "color-default" : "color-subtler"
                  )}
                >
                  {getPlaceholder()}
                </span>
              )}
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
                <Icon
                  icon={ChevronDown}
                  size={IconSizes.ExtraSmall}
                  className="color-icon-subtle group-disabled:color-icon-disabled"
                  aria-hidden="true"
                />
              </span>
              {keyboardShortcut && (
                <KeyboardTile
                  color="gray"
                  keyboardKey={keyboardShortcut?.toUpperCase()}
                  className="absolute inset-y-2 right-8 hidden group-hover:flex"
                />
              )}
            </ListboxButton>

            <ListboxOptions
              className={classNames(
                "bg-input-default shadow-control z-10 mt-2 max-h-60 overflow-auto rounded-sm ring-1 ring-black/5",
                !listBoxOptionsAnchor && "absolute w-full",
                optionsClassName
              )}
              modal={false}
              anchor={listBoxOptionsAnchor}
            >
              {isDeselectable && (
                <ListboxOption
                  className={({ selected, focus }) =>
                    classNames(
                      defaultOptionClassName,
                      focus && "bg-neutral-hovered",
                      selected && "bg-selected-alternate"
                    )
                  }
                  value={null}
                >
                  {({ selected }) => (
                    <div className="flex flex-row items-center gap-x-1">
                      <span
                        className={classNames(
                          "color-subtle block truncate",
                          selected && "color-default",
                          !!chipValueExtractor && "font-semibold"
                        )}
                      >
                        {deselectText ?? "None"}
                      </span>
                    </div>
                  )}
                </ListboxOption>
              )}
              {!!items &&
                renderHeadlessSafeItems(items).map((item) => (
                  <ListboxOption
                    key={keyExtractor(item)}
                    className={({ selected, focus }) =>
                      classNames(
                        defaultOptionClassName,
                        focus && "bg-neutral-hovered",
                        selected && "bg-selected-alternate"
                      )
                    }
                    value={item}
                  >
                    {({ selected }) => (
                      <div className="flex flex-row items-center gap-x-1">
                        {!!chipValueExtractor && (
                          <Chip color="gray" text={chipValueExtractor(item)} />
                        )}
                        {renderItem ? (
                          renderItem(item)
                        ) : (
                          <span
                            className={classNames(
                              "color-subtle block truncate",
                              selected && "color-default",
                              !!chipValueExtractor && "font-semibold"
                            )}
                          >
                            {valueExtractor(item)}
                          </span>
                        )}
                      </div>
                    )}
                  </ListboxOption>
                ))}
              {!!items && isHeadlessOptionsTruncated(items) && (
                <div
                  className={classNames(
                    defaultOptionClassName,
                    "text-body-sm color-subtle text-center"
                  )}
                >
                  Showing the first {HEADLESS_OPTIONS_LIMIT.toLocaleString()}{" "}
                  options.
                </div>
              )}
            </ListboxOptions>
          </div>
        </Label>
      </Listbox>
    </div>
  )
}
